import { useEffect, useState } from 'react';
import { getCookie } from '../../../../cloud/server/utils/index';

// Add this helper function near the top of the file
export const useAuthenticatedImageUrl = (originalUrl: string | undefined) => {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!originalUrl) {
      setBlobUrl(undefined);
      return;
    }

    // If it's already a blob, data URL, or local URL, use it directly
    if (
      originalUrl.startsWith('blob:') ||
      originalUrl.startsWith('data:') ||
      originalUrl.startsWith(window.location.origin)
    ) {
      setBlobUrl(originalUrl);
      return;
    }

    const fetchImage = async () => {
      try {
        const token = getCookie('login_token');
        const userId = getCookie('user_id');
        if (!token) {
          setBlobUrl(originalUrl); // fallback to original URL if no token
          return;
        }

        const response = await fetch(originalUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            'user-id': userId || '',
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        } else {
          console.warn('Failed to fetch authenticated image:', response.status);
          setBlobUrl(originalUrl); // fallback to original URL
        }
      } catch (error) {
        console.error('Error fetching authenticated image:', error);
        setBlobUrl(originalUrl); // fallback to original URL
      }
    };

    fetchImage().catch(() => {});

    // Cleanup function
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalUrl]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  return blobUrl;
};
